import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';
import { useBranches } from '../../api/queries/settings.queries';
import {
  ChevronDown,
  LogOut,
  ShoppingCart,
  Monitor,
  LayoutDashboard,
  Building2,
  User,
} from 'lucide-react';

interface POSLayoutProps {
  children: React.ReactNode;
}

function POSLayout({ children }: POSLayoutProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { selectedBranchId, selectedBranchName, setSelectedBranch } = useBranchStore();
  const { data: branches, isLoading: branchesLoading } = useBranches();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    setSelectedBranch(null, null);
    navigate('/login');
  };

  const handleBranchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const branchId = e.target.value;
    const branch = branches?.find(b => b._id === branchId);
    if (branch) {
      setSelectedBranch(branch._id, branch.name);
    }
  };

  // Check if user can select branch (HQ roles) or is locked to their branch
  const canSelectBranch = user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';

  // Check role permissions for quick links
  const canAccessPOS = user?.role && ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'].includes(user.role);
  const canAccessKDS = user?.role && ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'].includes(user.role);
  const canAccessAdmin = user?.role && ['HQ_ADMIN', 'HQ_PURCHASING', 'BRANCH_MANAGER', 'BRANCH_SUPERVISOR'].includes(user.role);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setShowLangMenu(false);
  };

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top Header Bar */}
      <header className="h-14 border-b bg-card flex items-center justify-between px-4 shrink-0">
        {/* Left - Branding */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">NT</span>
            </div>
            <div className="border-l border-border h-6 mx-1" />
            <span className="font-semibold text-lg">POS</span>
          </div>
        </div>

        {/* Right - Branch, Language & User */}
        <div className="flex items-center gap-2">
          {/* Branch Selector */}
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {canSelectBranch ? (
              branchesLoading ? (
                <span className="text-sm text-muted-foreground px-2">Loading branches...</span>
              ) : branches && branches.length > 0 ? (
                <select
                  value={selectedBranchId || ''}
                  onChange={handleBranchChange}
                  className="px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-w-[180px]"
                >
                  <option value="" disabled>Select Branch</option>
                  {branches
                    .filter(b => b.type === 'branch' || b.type === 'warehouse')
                    .map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                </select>
              ) : (
                <span className="text-sm text-orange-500 px-2">
                  No branches found - please run seed script
                </span>
              )
            ) : (
              <span className="text-sm font-medium px-2">
                {selectedBranchName || 'No branch'}
              </span>
            )}
          </div>

          <div className="h-6 border-l border-border mx-1" />

          {/* Language Switcher */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => {
                setShowLangMenu(!showLangMenu);
                setShowUserMenu(false);
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-background hover:bg-accent transition-colors"
            >
              <span className="text-base">{currentLang.flag}</span>
              <span className="text-sm font-medium hidden sm:inline">{currentLang.label}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>

            {showLangMenu && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg border bg-card shadow-lg z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      i18n.language === lang.code ? 'bg-accent' : ''
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="text-sm">{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => {
                setShowUserMenu(!showUserMenu);
                setShowLangMenu(false);
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-background hover:bg-accent transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                <span className="text-primary-foreground text-xs font-bold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium leading-none">{user?.username}</p>
                <p className="text-xs text-muted-foreground">{user?.role?.replace('_', ' ')}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-card shadow-lg z-50">
                {/* User Info Header */}
                <div className="px-4 py-3 border-b">
                  <p className="text-sm font-medium">{user?.username}</p>
                  <p className="text-xs text-muted-foreground">{user?.role?.replace(/_/g, ' ')}</p>
                </div>

                {/* Quick Access Links */}
                <div className="py-1 border-b">
                  {canAccessPOS && (
                    <Link
                      to="/pos"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors bg-accent"
                    >
                      <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                      <span>{t('nav.pos') || 'Point of Sale'}</span>
                    </Link>
                  )}
                  {canAccessKDS && (
                    <Link
                      to="/kds"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                    >
                      <Monitor className="h-4 w-4 text-muted-foreground" />
                      <span>{t('nav.kds') || 'Kitchen Display'}</span>
                    </Link>
                  )}
                  {canAccessAdmin && (
                    <Link
                      to="/orders"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                      <span>{t('nav.adminPanel') || 'Admin Panel'}</span>
                    </Link>
                  )}
                </div>

                {/* Profile Link */}
                <div className="py-1 border-b">
                  <Link
                    to="/profile"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                  >
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{t('common.profile')}</span>
                  </Link>
                </div>

                {/* Logout */}
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t('common.logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {children}
      </main>

      {/* Click outside to close menus */}
      {(showUserMenu || showLangMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowLangMenu(false);
          }}
        />
      )}
    </div>
  );
}

export default POSLayout;
